/**
 * 地区別データ設定 — userFields / postFields の完全 FieldDef 型 (Sprint BG-DataSettings)。
 *
 * 旧 v1 `old/packages/shared-types/src/index.ts` (L19-141, L1308-1337) を踏襲。
 * 旧 v1 では index.ts に直接書かれていた 9 型 FieldDef / 5 個 builtin user key /
 * `membershipFieldStorageTarget` を domain/dataSettings.ts に分離して整理。
 *
 * 旧との互換性:
 *   - `FieldType` に v2 専用の `'textarea'` を追加 (旧 9 型 + 1)。
 *     postFields の複数行 text 入力用、旧 v1 では 'text' で textarea を兼ねていた。
 *   - その他は旧と完全一致 (key/label/type/mode/options/optionsMeta/builtIn/description/
 *     definition/visible/displayOrder/useAsAnalysisAxis)。
 */
/**
 * データ型の日本語表示名。管理画面の型セレクトと AI プロンプトの【投稿データ】ブロックで
 * 同じ文言を使うため shared-types に置く (admin の `FIELD_TYPE_LABELS` は本定義の再エクスポート)。
 * 順序は管理画面の選択肢の並び順を兼ねるため変更しないこと。
 */
export const FIELD_TYPE_LABELS = {
    text: 'テキスト',
    textarea: '複数行テキスト',
    select: '単一選択',
    multiselect: '複数選択',
    number: '数値',
    checkbox: 'チェック',
    date: '日付',
    location: '位置情報',
    photo: '写真',
    'rating-scale': '段階スケール',
};
/**
 * 各データ型で許可される表示スタイル一覧。**先頭が既定値** (= 現行の見た目を維持する値)。
 *
 * 既定の選定根拠 (回帰なしを担保):
 *   - text → 'text-input' (現行の `<input type=text>` = 1 行)
 *   - textarea → 'textarea' (現行の `<textarea>`)
 *   - number → 'number-input' (現行の `<input type=number>`)
 *   - checkbox → 'checkbox' (現行のチェックボックス)
 *   - select → 'dropdown' (現行 optionsMeta 既定の `<select>`)
 *   - multiselect → 'checkboxes' (現行 optionsMeta 既定の縦並びチェックボックス)
 *   - rating-scale → 'buttons' (現行のボタン群)
 */
const ALLOWED_DISPLAY_STYLES = {
    text: ['text-input', 'textarea'],
    textarea: ['textarea', 'text-input'],
    number: ['number-input', 'slider', 'stepper'],
    date: ['date-input'],
    checkbox: ['checkbox', 'toggle'],
    select: ['dropdown', 'buttons', 'radio'],
    multiselect: ['checkboxes', 'buttons', 'dropdown'],
    'rating-scale': ['buttons', 'slider', 'stars'],
    location: ['location-picker'],
    photo: ['photo-picker'],
};
export function getAllowedDisplayStyles(type) {
    return ALLOWED_DISPLAY_STYLES[type];
}
/** データ型から既定の表示スタイルを返す (現状の挙動を維持する値 = 許可一覧の先頭)。 */
export function getDefaultDisplayStyle(type) {
    // ALLOWED_DISPLAY_STYLES は全 type で非空 (各 1 要素以上) を保証しているため [0] は必ず存在。
    return getAllowedDisplayStyles(type)[0];
}
/**
 * 旧 `optionsMeta.displayStyle` (select/multiselect 専用 3 値) を新 FieldDisplayStyle に橋渡しする。
 * top-level `displayStyle` 未設定の field に対して後方互換のため参照する。
 * select/multiselect 以外、または未設定なら undefined。
 */
function legacyOptionsMetaDisplayStyle(type, meta) {
    if (type !== 'select' && type !== 'multiselect')
        return undefined;
    const legacy = meta?.displayStyle;
    if (!legacy)
        return undefined;
    // select の旧 'checkboxes' (= 単一選択のチェックボックス風) は select の許可一覧に無いため
    // v2 では 'radio' (縦並び単一選択) に対応付ける。これを行わないと旧データが dropdown に
    // 静かに降格する (citizen-walk-reviewer 指摘の後方互換回帰)。
    // multiselect の 'checkboxes' は許可済みなのでそのまま。'dropdown' / 'buttons' は両型で有効。
    if (type === 'select' && legacy === 'checkboxes')
        return 'radio';
    return legacy;
}
/**
 * 表示スタイルがデータ型と整合しているかを確認し、整合しなければ既定にフォールバックする。
 * (データ型を後から変更したとき等に古い表示スタイルが残るのを防ぐ)
 *
 * 解決順 (top-level 優先):
 *   1. 引数 `style` (= field.displayStyle、top-level の新方式) が許可済みならそれ
 *   2. なければ後方互換で `optionsMeta.displayStyle` (select/multiselect 旧 3 値) を吸収
 *   3. いずれも無ければ `getDefaultDisplayStyle(type)`
 */
export function resolveDisplayStyle(type, style, optionsMeta) {
    const allowed = getAllowedDisplayStyles(type);
    if (style && allowed.includes(style))
        return style;
    const legacy = legacyOptionsMetaDisplayStyle(type, optionsMeta);
    if (legacy && allowed.includes(legacy))
        return legacy;
    return getDefaultDisplayStyle(type);
}
/**
 * FieldDef を直接渡して表示スタイルを解決する糖衣 (top-level displayStyle + optionsMeta 両考慮)。
 * UI 側は基本こちらを使う。
 */
export function resolveFieldDisplayStyle(field) {
    return resolveDisplayStyle(field.type, field.displayStyle, field.optionsMeta);
}
/** 表示スタイルの日本語ラベル (管理画面のセレクトボックスで使用)。 */
export const FIELD_DISPLAY_STYLE_LABELS = {
    textarea: 'テキストエリア（複数行）',
    'text-input': 'テキスト入力（1行）',
    'number-input': '数値入力',
    slider: 'スライダー',
    stepper: 'ステッパー（＋／−ボタン）',
    checkbox: 'チェックボックス',
    toggle: 'トグル（スイッチ）',
    buttons: 'ボタン（ピル型）',
    dropdown: 'ドロップダウン',
    radio: 'ラジオボタン',
    checkboxes: 'チェックボックスリスト',
    stars: '星評価',
    'date-input': '日付入力',
    'location-picker': '位置情報ピッカー',
    'photo-picker': '写真ピッカー',
};
/**
 * Sprint BG-DataSettings 27 — text / textarea 型の文字数 default 上限。
 * `FieldOptionsMeta.maxLength` 未指定の field に適用される。
 * type の意味的な目安: text=見出し相当 100 字、textarea=本文相当 1000 字。
 */
export const DEFAULT_TEXT_MAX_LENGTH = 100;
export const DEFAULT_TEXTAREA_MAX_LENGTH = 1000;
/**
 * Field の実効 maxLength を返す。text / textarea 以外は null。
 * 旧 PostForm が hardcode していた `TITLE_MAX` / `COMMENT_MAX` の代替として、
 * postFields[].optionsMeta.maxLength → type default → null の順で解決する。
 */
export function resolveFieldMaxLength(field) {
    if (field.type !== 'text' && field.type !== 'textarea')
        return null;
    if (typeof field.optionsMeta?.maxLength === 'number' && field.optionsMeta.maxLength > 0) {
        return field.optionsMeta.maxLength;
    }
    return field.type === 'text' ? DEFAULT_TEXT_MAX_LENGTH : DEFAULT_TEXTAREA_MAX_LENGTH;
}
/** value 配列を取り出す (マッチング・保存値の判定に使用)。 */
export function getFieldOptionValues(field) {
    return field.options.map((o) => o.value);
}
/** label 配列を取り出す (UI 描画用)。 */
export function getFieldOptionLabels(field) {
    return field.options.map((o) => o.label);
}
/** value から label を引く (不明値はそのまま返す)。 */
export function findFieldOptionLabel(field, value) {
    return field.options.find((o) => o.value === value)?.label ?? value;
}
/**
 * options を `FieldOption[]` 形式に正規化する。
 *
 * Firestore 上の旧データや CSV import 由来などで options が `string[]` (例: `["危険","資源","気づき"]`)
 * として保存されているケースがある。FieldOption.value/label は string 必須のため、
 * そのままだと UI が `o.value` / `o.label` を読めず select/multiselect ボタンが空欄表示になる。
 * 本関数は string でも `{value,label}` でも受け取り、正規化された配列を返す。
 */
export function normalizeFieldOptions(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const item of raw) {
        if (typeof item === 'string') {
            const s = item.trim();
            if (s)
                out.push({ value: s, label: s });
        }
        else if (item && typeof item === 'object') {
            const v = item.value;
            const l = item.label;
            const valueStr = typeof v === 'string' ? v : '';
            const labelStr = typeof l === 'string' ? l : valueStr;
            if (valueStr)
                out.push({ value: valueStr, label: labelStr || valueStr });
        }
    }
    return out;
}
/** FieldDef の options を `normalizeFieldOptions` 経由で正規化したコピーを返す。 */
export function normalizeFieldDef(field) {
    return { ...field, options: normalizeFieldOptions(field.options) };
}
/** FieldDef[] を一括正規化する。 */
export function normalizeFieldDefs(fields) {
    if (!fields || !Array.isArray(fields))
        return [];
    return fields.map((f) => normalizeFieldDef(f));
}
/**
 * `useAsAnalysisAxis` の既定挙動。
 * - postFields 由来 かつ type ∈ {select, rating-scale, multiselect} → true
 * - userFields 由来 → false (明示的に true を付けたものだけ軸として使う)
 * - その他 → false
 *
 * field.useAsAnalysisAxis が明示設定されていればそれを尊重する。
 */
export function resolveUseAsAnalysisAxis(field, source) {
    if (typeof field.useAsAnalysisAxis === 'boolean')
        return field.useAsAnalysisAxis;
    if (source !== 'postFields')
        return false;
    return field.type === 'select' || field.type === 'rating-scale' || field.type === 'multiselect';
}
/** 組み込み (builtIn=true) の userField key で、Membership 専用カラムに保存される key。 */
export const BUILTIN_USER_FIELD_KEYS = new Set([
    'nickname',
    'gender',
    'ageGroup',
    'relationship',
    'attribute',
]);
/** 保存値 (Firestore から読んだ unknown) を MembershipProfileValue に整える。形が違えば null。 */
export function readMembershipProfileValue(value) {
    if (typeof value === 'string' || typeof value === 'boolean')
        return value;
    if (typeof value === 'number')
        return Number.isFinite(value) ? value : null;
    if (Array.isArray(value))
        return value.filter((v) => typeof v === 'string');
    return null;
}
/** 保存用に整える。空文字・空配列は null (未回答)、文字列は前後の空白を落とす。 */
export function toStoredMembershipProfileValue(value) {
    const v = readMembershipProfileValue(value);
    if (typeof v === 'string')
        return v.trim() === '' ? null : v.trim();
    if (Array.isArray(v)) {
        const items = v.map((x) => x.trim()).filter((x) => x !== '');
        return items.length === 0 ? null : items;
    }
    return v;
}
/** 表示・CSV・AI 等で文字として使う。複数選択は「 / 」区切り、チェックは「はい / いいえ」。 */
export function formatMembershipProfileValue(value) {
    const v = readMembershipProfileValue(value);
    if (v === null)
        return '';
    if (Array.isArray(v))
        return v.filter((x) => x !== '').join(' / ');
    if (typeof v === 'boolean')
        return v ? 'はい' : 'いいえ';
    return String(v);
}
/**
 * 文字で受け取った値 (CSV 取り込み等) を項目の型に合わせる。CSV 書き出しと対になる形:
 * 複数選択は「/」区切りを配列に、数値・段階評価は数値に、チェックは はい/いいえ を真偽値に。
 * 型に合わない値は文字のまま返す (情報を落とさない)。空は null。
 */
export function coerceMembershipProfileValue(field, raw) {
    const text = (raw ?? '').trim();
    if (text === '')
        return null;
    switch (field?.type) {
        case 'multiselect': {
            const items = text
                .split('/')
                .map((x) => x.trim())
                .filter((x) => x !== '');
            return items.length > 0 ? items : null;
        }
        case 'number':
        case 'rating-scale': {
            const n = Number(text);
            return Number.isFinite(n) ? n : text;
        }
        case 'checkbox':
            if (['はい', 'true', '1'].includes(text.toLowerCase()))
                return true;
            if (['いいえ', 'false', '0'].includes(text.toLowerCase()))
                return false;
            return text;
        default:
            return text;
    }
}
/** `sessions` は joinedSessionIds に格納される特殊扱いの key。 */
export const SESSIONS_FIELD_KEY = 'sessions';
/**
 * 投稿タイプ (防災の「危険 / 資源 / 気づき」、子ども向けの「ホッと / ハッと / ヒント」) の入力項目の内部キー。
 * 値は投稿の `extraFields.postType` に入る。2026-10-07 に `category` から改名 (AI 解析のカテゴリマスタの
 * 「対象分類」と用語が混ざるため)。既存データは `migrate-post-type-key.mjs` で移行。
 */
export const POST_TYPE_FIELD_KEY = 'postType';
export function membershipFieldStorageTarget(field) {
    if (field.key === SESSIONS_FIELD_KEY)
        return { kind: 'sessions' };
    if (field.builtIn && BUILTIN_USER_FIELD_KEYS.has(field.key)) {
        return {
            kind: 'builtin',
            column: field.key,
        };
    }
    return { kind: 'custom' };
}
//# sourceMappingURL=dataSettings.js.map